import { Instant } from "@js-joda/core";
import { createMutationGroup } from "../../core/api/mutations";
import { BodyOf } from "../../core/api/api-utils";
import {
  PatientContractDiagnosisCodeId,
  PatientDocumentAiReviewProcessQuestionRejectionId,
  PatientDocumentScheduleId,
  PatientDocumentTypeId,
  PatientTaskInstanceId,
} from "../../shared/schema/schema";

import type {
  ContractDiagnosisCodeCreateBody,
  ContractDiagnosisCodeEditBody,
} from "./entity-editors/contractDiagnosisCodeForm";

// The create / edit routes of patient contract diagnosis codes are classic-router routes, absent
// from the generated schema: their bodies are typed from the schema's PatientContractDiagnosisCode,
// and their (unrevived) responses are not used — callers refetch the list.
const CONTRACT_DIAGNOSIS_CODES_PATH =
  "/agencies/:agencyId/agency_members/:agencyMemberId/patient_contract_diagnosis_codes";

type SaveAnswerBody = Pick<
  BodyOf<
    "post",
    "./patient_task_instances/:patientTaskInstanceId/patient_documents/:documentTypeId/answer"
  >,
  "questionId" | "answer" | "chartRow"
>;

export type SubmitDocumentBody = Pick<
  BodyOf<
    "post",
    "./patient_task_instances/:patientTaskInstanceId/patient_documents/:documentTypeId/submit"
  >,
  "keepOriginalSubmissionDate" | "submittedDate"
>;

// NOTE: createMutation only carries mutationFn, so callers invalidate
// [DOCUMENT_REVIEW_ROOT_QUERY_KEY] ("patientTaskRegulars") in useMutation's onSuccess.
export default createMutationGroup(({ api, createMutation }) => ({
  saveAnswer: (taskInstanceId: PatientTaskInstanceId, documentTypeId: PatientDocumentTypeId) =>
    createMutation({
      mutationFn: (body: SaveAnswerBody) =>
        api.post(
          "./patient_task_instances/:patientTaskInstanceId/patient_documents/:documentTypeId/answer",
          {
            path: { patientTaskInstanceId: taskInstanceId, documentTypeId },
            body: { ...body, createdAt: Instant.now() },
          }
        ),
    }),

  /**
   * The classic form's Submit: submits the task's document with its answers. The PDF is generated
   * once the task is complete. `keepOriginalSubmissionDate` keeps the submitted date and submitter
   * of a document submitted before; `submittedDate` overrides the submitted date (start of that
   * day in the agency's timezone).
   */
  submit: (taskInstanceId: PatientTaskInstanceId, documentTypeId: PatientDocumentTypeId) =>
    createMutation({
      mutationFn: (body: SubmitDocumentBody) =>
        api.post(
          "./patient_task_instances/:patientTaskInstanceId/patient_documents/:documentTypeId/submit",
          {
            path: { patientTaskInstanceId: taskInstanceId, documentTypeId },
            body: { ...body, makeCopy: false, forceGeneratePDF: false },
          }
        ),
    }),

  reopenWithComments: (scheduledDocId: PatientDocumentScheduleId) =>
    createMutation({
      mutationFn: (
        body: BodyOf<"post", "./patient_documents/:scheduledDocId/reopen_with_comments">
      ) =>
        api.post("./patient_documents/:scheduledDocId/reopen_with_comments", {
          path: { scheduledDocId },
          body,
        }),
    }),

  withdrawComment: (scheduledDocId: PatientDocumentScheduleId) =>
    createMutation({
      mutationFn: (rejectionId: PatientDocumentAiReviewProcessQuestionRejectionId) =>
        api.post("./patient_documents/:scheduledDocId/question_comments/:rejectionId/withdraw", {
          path: { scheduledDocId, rejectionId },
        }),
    }),

  createContractDiagnosisCode: () =>
    createMutation({
      mutationFn: (body: ContractDiagnosisCodeCreateBody) =>
        api.anyRequest<unknown>("POST", CONTRACT_DIAGNOSIS_CODES_PATH, {
          body,
        }),
    }),

  updateContractDiagnosisCode: () =>
    createMutation({
      mutationFn: (params: {
        id: PatientContractDiagnosisCodeId;
        body: ContractDiagnosisCodeEditBody;
      }) =>
        api.anyRequest<unknown>(
          "PUT",
          `${CONTRACT_DIAGNOSIS_CODES_PATH}/:patientContractDiagnosisCodeId`,
          { path: { patientContractDiagnosisCodeId: params.id }, body: params.body }
        ),
    }),

  deleteContractDiagnosisCode: () =>
    createMutation({
      mutationFn: (patientContractDiagnosisCodeId: PatientContractDiagnosisCodeId) =>
        api.delete("./patient_contract_diagnosis_codes/:patientContractDiagnosisCodeId", {
          path: { patientContractDiagnosisCodeId },
        }),
    }),

  regeneratePdf: (scheduledDocId: PatientDocumentScheduleId) =>
    createMutation({
      mutationFn: () =>
        api.post("./patient_documents/:scheduledDocId/regenerate_pdf", {
          path: { scheduledDocId },
        }),
    }),
}));
